import type { DeskBoardCard, DeskOperator, DeskSummary } from '@evogard/contract';
import {
  DESK_BAND_LABELS,
  ZERO,
  deskColumnTitle,
  deskOperatorColumnTitle,
  deskOperatorGroup,
  fromColumn,
  sum,
  type DeskFlow,
  type DeskOperatorGroup,
  type Money,
} from '@evogard/domain';

/**
 * O que junta as bases no quadro da Mesa.
 *
 * A API responde uma base por chamada (`/billing/desk/:tenant/...`) e a tela
 * mostra CE e GO no mesmo quadro (decisão de 19/10/2026). Somar as colunas e
 * unir os operadores das bases mora aqui; intercalar as páginas é
 * `mergeDeskStreams`, em `@evogard/domain`, que tem teste.
 */

export interface DeskTenant {
  id: number;
  slug: string;
  name: string;
}

/** Um card, com a base de onde ele veio — o selo do canto e o endereço dos atos. */
export interface BoardCard extends DeskBoardCard {
  tenant: DeskTenant;
}

/** Uma base que respondeu o resumo. */
export interface TenantSummary {
  tenant: DeskTenant;
  summary: DeskSummary;
}

/** Contagem e valor de uma coluna, somados nas bases. */
export function columnTotals(
  summaries: readonly TenantSummary[],
  key: string,
): { count: number; amount: Money } {
  let count = 0;
  let amount = ZERO;
  for (const { summary } of summaries) {
    const column = summary.columns.find((c) => c.key === key);
    if (!column) continue;
    count += column.count;
    amount = sum(amount, fromColumn(column.amount) ?? ZERO);
  }
  return { count, amount };
}

/** Um operador do quadro, com as bases em que ele aparece. */
export interface BoardOperator extends DeskOperator {
  tenants: string[];
}

/**
 * Os operadores das bases, sem repetir quem está nas duas: a coluna de uma
 * pessoa é uma só, com os cards da CE e da GO dentro. Os fluxos somam — quem é
 * ativo numa base e receptivo na outra tem as duas colunas.
 */
export function mergeOperators(summaries: readonly TenantSummary[]): BoardOperator[] {
  const byUser = new Map<string, BoardOperator>();
  for (const { tenant, summary } of summaries) {
    for (const o of summary.operators) {
      const known = byUser.get(o.userId);
      if (!known) {
        byUser.set(o.userId, { ...o, tenants: [tenant.slug] });
        continue;
      }
      known.activeFlow ||= o.activeFlow;
      known.receptiveFlow ||= o.receptiveFlow;
      known.listed ||= o.listed;
      known.tenants.push(tenant.slug);
    }
  }
  return [...byUser.values()].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}

/**
 * A ordem das colunas de operador numa faixa: a da pessoa logada primeiro, logo
 * depois da fila mãe, e as demais por nome — cada um vê a própria coluna no
 * começo (pedido do dono, 20/10/2026). Quem não tem coluna, o admin, vê todas
 * por nome.
 */
export function orderOperators<T extends { userId: string }>(operators: readonly T[], me: string): T[] {
  return [...operators.filter((o) => o.userId === me), ...operators.filter((o) => o.userId !== me)];
}

/**
 * As faixas do quadro, na ordem: cada uma com a fila mãe e as colunas dos
 * colaboradores, e um fundo próprio (decisão do dono de 20/10/2026: a cor diz
 * a urgência).
 *
 * As classes vão por extenso porque o Tailwind só emite a classe que lê no
 * fonte (`ColorPicker.tsx`, `COLOR_CLASSES`). As `fase-*-suave` são as cores
 * de fundo de coluna do tema, e `theme.test.ts` mede `texto` e `texto-suave`
 * sobre cada uma, no claro e no escuro.
 */
export const DESK_SECTIONS = {
  due: {
    title: 'Vencimento',
    subtitle: 'Até 5 dias de atraso, pelo dia do vencimento',
    tone: 'bg-fase-azul-suave',
  },
  late: {
    title: 'Atraso de 6 a 30 dias',
    subtitle: 'A fila mãe é repartida por quem distribui',
    tone: 'bg-fase-amarelo-suave',
  },
  longLate: {
    title: '31 dias ou mais',
    subtitle: 'A fila mãe é repartida por quem distribui',
    tone: 'bg-fase-vermelho-suave',
  },
  receptive: {
    title: 'Receptiva',
    subtitle: 'Quem ligou para a cobrança',
    tone: 'bg-fase-roxo-suave',
  },
  paid: {
    title: 'Pagos',
    subtitle: 'No período dos filtros',
    tone: 'bg-fase-verde-suave',
  },
} as const;

/**
 * O título da coluna de um operador — "Cobrança - {nome}" no legado. Mora em
 * `@evogard/domain` desde 20/10/2026, porque o histórico do card e o log,
 * descritos no servidor, dizem o mesmo nome.
 */
export function operatorColumnTitle(group: DeskOperatorGroup, name: string): string {
  return deskOperatorColumnTitle(group, name);
}

export { DESK_BAND_LABELS };

/**
 * O nome da coluna de uma chave de `deskColumnKey`, como o quadro a mostra.
 * Na coluna de operador, `operatorName` é o dono do card.
 */
export function columnTitle(key: string | null, operatorName: string | undefined): string {
  return deskColumnTitle(key, operatorName);
}

/**
 * Onde o card está — "Fila mãe — Vencimento 15", "Atraso 6-30 — Claudenir",
 * "Pago cobrança". Sai da coluna que o SERVIDOR calculou (`card.column`), e não
 * de uma conta da tela: é a mesma chave que o quadro usa para desenhar.
 */
export function cardPlace(card: Pick<DeskBoardCard, 'column' | 'operator'>): string {
  return columnTitle(card.column, card.operator?.name);
}

/**
 * Para onde o card vai se passar a `operator` (ou voltar à fila, com `null`).
 *
 * Não se escolhe a faixa: ela sai do vencimento, e o card cai na coluna da
 * pessoa que corresponde a ela — vencimento, 6-30 ou 31+. A receptiva não tem
 * fila; vai para a receptiva da pessoa.
 */
export function destinationOf(
  card: Pick<DeskBoardCard, 'flow' | 'band'>,
  operator: Pick<DeskOperator, 'name'> | null,
): string {
  if (card.flow === 'receptiva') {
    return operator === null ? 'Receptiva' : operatorColumnTitle('receptiva', operator.name);
  }
  if (card.band === null) return operator === null ? 'Fila mãe' : operator.name;
  if (operator === null) return `Fila mãe — ${DESK_BAND_LABELS[card.band]}`;
  return operatorColumnTitle(deskOperatorGroup(card.band), operator.name);
}

/**
 * A pessoa atende aquele fluxo nesta base — está na lista de operadores da
 * Mesa. É quem puxa atendimento: o admin, fora da lista, não puxa, como no
 * legado (`App.tsx:313`). O servidor confere de novo.
 */
export function attends(summary: DeskSummary, me: string, flow: DeskFlow): boolean {
  return summary.operators.some(
    (o) => o.userId === me && o.listed && (flow === 'ativa' ? o.activeFlow : o.receptiveFlow),
  );
}

/** O "Puxar atendimento" do card: fila de vencimento, sem dono, e quem clica atende. */
export function canPull(card: DeskBoardCard, summary: DeskSummary, me: string): boolean {
  return (
    card.closedAt === null &&
    card.flow === 'ativa' &&
    card.operator === null &&
    (card.column ?? '').startsWith('fila:vencimento_') &&
    summary.can.assign &&
    attends(summary, me, 'ativa')
  );
}

/** O que a API recusou, do jeito que ela disse — senão, o padrão. */
export function failure(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}
